/**
 * PlacementBadgeLayer — renders transient "+N" badges at territory centroids
 * when armies are placed during the reinforce phase.
 *
 * Each badge pops in (scale 0.5→1 + opacity 0→1), floats upward, and fades out
 * over 1000 ms total. One badge per territory entry; multiple badges animate
 * independently with their own shared values.
 *
 * Reduced motion: no pop-in scale, no upward float — just a straight opacity
 * 0→1→0 cycle over 800 ms.
 *
 * Placed ABOVE ConquestFlashLayer and BELOW sea-route lines in the layer stack.
 * pointerEvents="none" / accessibilityElementsHidden so touches and screen
 * readers pass through to territory polygons.
 */

import React from 'react'
import { Platform } from 'react-native'
import { Text as SvgText, G } from 'react-native-svg'
import Animated, {
  useSharedValue,
  useAnimatedProps,
  withSequence,
  withTiming,
  Easing,
  createAnimatedComponent,
} from 'react-native-reanimated'
import { useReducedMotion } from 'react-native-reanimated'
import type { PlacementAnimationEntry } from '../../hooks/usePlacementEvents'

const AnimatedText = createAnimatedComponent(SvgText)

// ─── Individual badge ─────────────────────────────────────────────────────────

interface PlacementBadgeProps {
  cx: number
  cy: number
  armies: number
  playerColor: string
  badgeFontSize: number
  reducedMotion: boolean
}

function PlacementBadge({
  cx,
  cy,
  armies,
  playerColor,
  badgeFontSize,
  reducedMotion,
}: PlacementBadgeProps) {
  // Vertical offset: animated from 0 → -0.5 × badgeFontSize (float upward)
  const floatOffset = useSharedValue(0)
  const opacity = useSharedValue(0)

  React.useEffect(() => {
    if (reducedMotion) {
      // Reduced motion: fade in → hold → fade out, no movement
      opacity.value = withSequence(
        withTiming(1, { duration: 100 }),
        withTiming(1, { duration: 600 }),
        withTiming(0, { duration: 100 }),
      )
      // No float
      floatOffset.value = 0
    } else {
      // Full animation:
      // 0–60ms: pop-in (opacity 0→1, scale is handled via fontSize trick below)
      // 60–360ms: hold at full opacity, float upward
      // 360–760ms: fade out
      opacity.value = withSequence(
        withTiming(1, { duration: 60, easing: Easing.out(Easing.back(1.5)) }),
        withTiming(1, { duration: 300 }),
        withTiming(0, { duration: 400, easing: Easing.in(Easing.quad) }),
      )
      floatOffset.value = withSequence(
        withTiming(0, { duration: 60 }),
        withTiming(-badgeFontSize * 0.5, { duration: 300, easing: Easing.out(Easing.quad) }),
        withTiming(-badgeFontSize * 0.5, { duration: 400 }),
      )
    }
    // Only runs on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const outlineAnimatedProps = useAnimatedProps(() => ({
    opacity: opacity.value,
    y: cy - badgeFontSize * 1.5 + floatOffset.value,
  }))

  const fillAnimatedProps = useAnimatedProps(() => ({
    opacity: opacity.value,
    y: cy - badgeFontSize * 1.5 + floatOffset.value,
  }))

  const label = `+${armies}`

  return (
    <G
      pointerEvents="none"
      {...(Platform.OS === 'ios' && { accessibilityElementsHidden: true })}
      {...(Platform.OS === 'android' && { importantForAccessibility: 'no-hide-descendants' as const })}
      {...(Platform.OS === 'web' && ({ 'aria-hidden': true } as { 'aria-hidden': true }))}
    >
      {/* Outline (stroke behind fill) */}
      <AnimatedText
        x={cx}
        y={cy - badgeFontSize * 1.5}
        fill="#000000"
        stroke="#00000066"
        strokeWidth={badgeFontSize * 0.32}
        strokeLinejoin="round"
        fontSize={badgeFontSize}
        fontWeight="800"
        textAnchor="middle"
        animatedProps={outlineAnimatedProps}
      >
        {label}
      </AnimatedText>
      {/* Fill */}
      <AnimatedText
        x={cx}
        y={cy - badgeFontSize * 1.5}
        fill={playerColor}
        fontSize={badgeFontSize}
        fontWeight="800"
        textAnchor="middle"
        animatedProps={fillAnimatedProps}
      >
        {label}
      </AnimatedText>
    </G>
  )
}

// ─── Layer ────────────────────────────────────────────────────────────────────

interface PlacementBadgeLayerProps {
  entries: PlacementAnimationEntry[]
  /** Territory centroid lookup: territory id → {x, y} */
  positionById: Record<string, { x: number; y: number } | undefined>
  /** Badge font size in SVG viewBox units (same as MapView's badgeFontSize). */
  badgeFontSize: number
}

export function PlacementBadgeLayer({
  entries,
  positionById,
  badgeFontSize,
}: PlacementBadgeLayerProps) {
  const reducedMotion = useReducedMotion()

  if (entries.length === 0) return null

  return (
    <>
      {entries.map((entry) => {
        const pos = positionById[entry.territoryId]
        if (!pos) return null
        return (
          <PlacementBadge
            key={`placement-${entry.territoryId}-${entry.startedAt}`}
            cx={pos.x}
            cy={pos.y}
            armies={entry.armies}
            playerColor={entry.playerColor}
            badgeFontSize={badgeFontSize}
            reducedMotion={reducedMotion}
          />
        )
      })}
    </>
  )
}
